import React from 'react';
import { DownloadIcon, SearchIcon } from 'lucide-react';
import { usePortalAuth } from '../../context/PortalAuth';
import { DELIVERABLES, PROJECTS } from '../../data/portal';
import { requestDownloadUrl } from '../../lib/leads';
import { track } from '../../lib/analytics';
export function PortalDeliverables() {
  const {
    user
  } = usePortalAuth();
  const [query, setQuery] = React.useState('');
  const [log, setLog] = React.useState<string[]>([]);
  if (!user) return null;
  const projectIds = PROJECTS.filter(project => project.organisationId === user.organisationId).map(project => project.id);
  const term = query.trim().toLowerCase();
  const files = DELIVERABLES.filter(file => projectIds.includes(file.projectId)).filter(file => !term || file.name.toLowerCase().includes(term) || file.type.toLowerCase().includes(term));
  const download = async (fileName: string, projectId: string) => {
    const result = await requestDownloadUrl(fileName, {
      kind: 'deliverable',
      id: projectId
    });
    if (!result.url) {
      setLog(current => [`${new Date().toLocaleTimeString('en-GB')} · ${result.error ?? 'Download refused'} · ${fileName}`, ...current]);
      return;
    }
    track('deliverable_downloaded', {
      project: projectId,
      file: fileName
    });
    setLog(current => [`${new Date().toLocaleTimeString('en-GB')} · ${user.email} · ${fileName}`, ...current]);
    window.open(result.url, '_blank', 'noopener');
  };
  return <div className="space-y-8" data-id="element-951" data-mp="2qhw5y9q8l1ne">
      <header data-id="element-952" data-mp="2qhmfym56yuhp">
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-gold" data-id="element-953" data-mp="2qh2tcs4ljhxc">Deliverables</p>
        <h1 className="mt-3 font-display text-[30px] leading-tight text-ink sm:text-[36px]" data-id="element-954" data-mp="2mh2ghbx5bese">Your file library</h1>
        <p className="mt-2 max-w-prose text-[16px] text-body" data-id="element-955" data-mp="2hk2af10aqc1a">
          Every download is issued through a link signed for that request and valid for fifteen minutes. Downloads
          are logged against your account.
        </p>
      </header>

      <div className="rounded-md border border-line bg-white p-6" data-id="element-956" data-mp="ytwqs8c92cga">
        <label htmlFor="deliverable-search" className="block text-[14px] font-medium text-ink" data-id="element-957" data-mp="13lo9xxt2tfwe">
          Search files
        </label>
        <div className="relative mt-2" data-id="element-958" data-mp="pvccdjdq2oxv">
          <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" aria-hidden="true" data-id="element-959" data-mp="ps6qed0e436d" />
          <input id="deliverable-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search by name or file type" className="w-full rounded-sm border border-line py-2.5 pl-9 pr-3 text-[15px] text-ink" data-id="element-960" data-mp="1ik9unmhxolqm" />
        </div>

        <p aria-live="polite" className="mt-4 text-small text-muted" data-id="element-961" data-mp="3tb002eq1s7v9">
          {files.length} {files.length === 1 ? 'file' : 'files'}
        </p>

        <div className="mt-4 overflow-x-auto" data-id="element-962" data-mp="25i0b41894eo">
          <table className="w-full min-w-[620px] border-collapse text-left" data-id="element-963" data-mp="3qqk3scsx1y5u">
            <caption className="sr-only" data-id="element-964" data-mp="3qsoi7oofyfkd">Deliverables available to your organisation</caption>
            <thead data-id="element-965" data-mp="3lr7jnztfj56y">
              <tr className="border-b border-line text-small uppercase tracking-[0.12em] text-muted" data-id="element-966" data-mp="3lttr7zgblkp6">
                <th scope="col" className="py-3 pr-4 font-medium" data-id="element-967" data-mp="3us5noi3vbcpj">File</th>
                <th scope="col" className="py-3 pr-4 font-medium" data-id="element-968" data-mp="3usyoz3k25a0a">Type</th>
                <th scope="col" className="py-3 pr-4 font-medium" data-id="element-969" data-mp="373e42f4cg2od">Version</th>
                <th scope="col" className="py-3 pr-4 font-medium" data-id="element-970" data-mp="331sj06hnoi33">Size</th>
                <th scope="col" className="py-3 pr-4 font-medium" data-id="element-971" data-mp="3gs4b45e8qgu8">Added</th>
                <th scope="col" className="py-3 font-medium" data-id="element-972" data-mp="3guh37bdpq7l0">
                  <span className="sr-only" data-id="element-973" data-mp="3c0w0x7536rg0">Download</span>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line" data-id="element-974" data-mp="2oaifw0kx5sux">
              {files.map(file => <tr key={file.id} data-id="element-975" data-mp="2jcgzxoisi9no">
                  <td className="py-4 pr-4 text-[15px] text-ink" data-id="element-976" data-mp="2jb5tti1vgpte">{file.name}</td>
                  <td className="py-4 pr-4 text-[14px] text-body" data-id="element-977" data-mp="2wxmhmm2r0fw3">{file.type}</td>
                  <td className="py-4 pr-4 text-[14px] text-body" data-id="element-978" data-mp="31zbt22tvuvkp">{file.version}</td>
                  <td className="py-4 pr-4 text-[14px] text-body" data-id="element-979" data-mp="36x4z8vv6rpkz">{(file.sizeKb / 1024).toFixed(1)} MB</td>
                  <td className="py-4 pr-4 text-[14px] text-body" data-id="element-980" data-mp="3bpeu8qb5rfu4">
                    {new Date(file.createdAt).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric'
                })}
                  </td>
                  <td className="py-4" data-id="element-981" data-mp="3kngvn6yt0ahx">
                    <button type="button" onClick={() => download(file.fileName, file.projectId)} className="inline-flex items-center gap-2 rounded-sm border border-royal/30 px-3.5 py-2 text-[14px] text-royal transition-colors hover:bg-mist" data-id="element-982" data-mp="3poo3t74uzbp4">
                      <DownloadIcon className="h-4 w-4" aria-hidden="true" data-id="element-983" data-mp="20a9h0sdehyw9" />
                      Download
                      <span className="sr-only" data-id="element-984" data-mp="hfwuw9c8lyun">{file.name}</span>
                    </button>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>

        {files.length === 0 && <p className="mt-6 rounded-sm border border-dashed border-line p-8 text-center text-[15px] text-muted" data-id="element-985" data-mp="8jnyl3jk3gcq">
            No files match that search.
          </p>}
      </div>

      {log.length > 0 && <section aria-labelledby="download-log" className="rounded-md border border-line bg-white p-6" data-id="element-986" data-mp="wbw27hrivuyq">
          <h2 id="download-log" className="font-display text-[20px] text-ink" data-id="element-987" data-mp="rc06s6ww5bus">
            Download log, this session
          </h2>
          <ul className="mt-4 space-y-2 text-small text-muted" data-id="element-988" data-mp="mcna6ug7wqf0">
            {log.map(entry => <li key={entry} data-id="element-989" data-mp="3c3jz5d6jdnhj">{entry}</li>)}
          </ul>
        </section>}
    </div>;
}